import { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, Loader2, CheckCircle2, RefreshCw, AlertTriangle, X } from 'lucide-react';

const API = '/api';

const CATEGORY_LABELS = {
  person_name:      'Named person',
  person_count:     'Count of people',
  location:         'Location / region',
  date_event:       'Date or event',
  partnership:      'Partnership',
  new_product:      'New product / launch',
  policy_offering:  'Policy / offer',
  number_stat:      'Number / stat',
  quote:            'Quoted speech',
  award_status:     'Award / status',
  unspecified:      'Specific claim',
};

/**
 * ClaimAudit — gates publishing on factual verification.
 *
 * Props:
 *   text        : the current draft text
 *   onTextChange: (newText) => void  (called when user accepts a generic rewrite
 *                                     for a claim; we replace the claim's span)
 *   testid      : optional testid prefix
 *   onAudited   : optional callback (claims, allVerified) => void
 *
 * Flow:
 *   1. User clicks "Audit claims" → backend extracts each falsifiable claim
 *   2. User ticks "verified" per claim (claims they confirm are true) OR
 *      clicks "Rewrite generic" to replace the claim with a neutral statement
 *   3. Until every must_verify claim is either ticked or rewritten, the
 *      "Ready to publish" badge stays red
 */
export default function ClaimAudit({ text, onTextChange, testid = 'claim-audit', onAudited }) {
  const [claims, setClaims] = useState(null);   // null = not run yet
  const [verified, setVerified] = useState({}); // claim_id -> bool
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rewriting, setRewriting] = useState({});

  const runAudit = async () => {
    if (!text || !text.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/voice/claim-audit`, { text });
      const list = res.data?.claims || [];
      setClaims(list);
      setVerified({});
      if (onAudited) onAudited(list, false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Claim audit failed. Please try again.');
    }
    setLoading(false);
  };

  const toggleVerified = (id) => {
    const next = { ...verified, [id]: !verified[id] };
    setVerified(next);
    if (onAudited && claims) {
      const allOK = claims
        .filter((c) => c.severity === 'must_verify')
        .every((c) => next[c.id]);
      onAudited(claims, allOK);
    }
  };

  const handleRewrite = async (claim) => {
    setRewriting({ ...rewriting, [claim.id]: true });
    try {
      const res = await axios.post(`${API}/voice/rewrite-claim`, {
        full_text: text,
        claim_text: claim.text,
      });
      const newSentence = (res.data?.rewritten || '').trim();
      if (newSentence) {
        // Try span-based replacement first; fall back to find-and-replace
        let newText = text;
        if (claim.span && claim.span.start >= 0 && claim.span.end > claim.span.start) {
          newText = text.slice(0, claim.span.start) + newSentence + text.slice(claim.span.end);
        } else if (text.includes(claim.text)) {
          newText = text.replace(claim.text, newSentence);
        }
        if (onTextChange) onTextChange(newText);
        // Mark this claim "verified" (handled) so it stops blocking publish
        const next = { ...verified, [claim.id]: true };
        setVerified(next);
        if (onAudited && claims) {
          const allOK = claims
            .filter((c) => c.severity === 'must_verify')
            .every((c) => next[c.id]);
          onAudited(claims, allOK);
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Rewrite failed.');
    }
    setRewriting({ ...rewriting, [claim.id]: false });
  };

  const mustVerify = (claims || []).filter((c) => c.severity === 'must_verify');
  const verifiedCount = mustVerify.filter((c) => verified[c.id]).length;
  const allVerified = mustVerify.length > 0 && verifiedCount === mustVerify.length;
  const noClaims = claims !== null && claims.length === 0;

  return (
    <div data-testid={testid} className="bg-hg-bg border border-hg-border rounded-sm p-3 space-y-2.5">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {noClaims ? (
            <>
              <ShieldCheck size={14} className="text-hg-green" strokeWidth={2} />
              <span className="text-xs font-sans text-hg-green uppercase tracking-wider">No factual claims to verify</span>
            </>
          ) : allVerified ? (
            <>
              <ShieldCheck size={14} className="text-hg-green" strokeWidth={2} />
              <span className="text-xs font-sans text-hg-green uppercase tracking-wider">All claims verified — ready to publish</span>
            </>
          ) : claims === null ? (
            <>
              <ShieldAlert size={14} className="text-hg-text-dim" strokeWidth={2} />
              <span className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Claim Audit</span>
            </>
          ) : (
            <>
              <ShieldAlert size={14} className="text-yellow-400" strokeWidth={2} />
              <span className="text-xs font-sans text-yellow-400 uppercase tracking-wider">
                {verifiedCount}/{mustVerify.length} verified — verify or rewrite each before publishing
              </span>
            </>
          )}
        </div>
        <button
          data-testid={`${testid}-run-btn`}
          onClick={runAudit}
          disabled={loading || !text?.trim()}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green disabled:opacity-40"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} strokeWidth={1.5} />}
          {claims === null ? 'Audit claims' : 'Re-audit'}
        </button>
      </div>

      <p className="text-[10px] text-hg-text-dim font-sans leading-relaxed">
        Before sending, confirm each specific factual claim below is true — or click <span className="text-hg-text">Rewrite generic</span> to replace it with a neutral statement that doesn't make the claim.
      </p>

      {/* Claim list */}
      {claims && claims.length > 0 && (
        <ul data-testid={`${testid}-list`} className="space-y-1.5">
          {claims.map((c, idx) => {
            const isVerified = !!verified[c.id];
            const cat = CATEGORY_LABELS[c.category] || c.category;
            return (
              <li
                key={c.id}
                data-testid={`${testid}-claim-${idx}`}
                className={`rounded-sm border p-2.5 ${isVerified ? 'border-hg-green/40 bg-hg-green/5' : 'border-yellow-500/30 bg-yellow-500/5'}`}
              >
                <div className="flex items-start gap-2">
                  <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-0">
                    <input
                      data-testid={`${testid}-claim-${idx}-check`}
                      type="checkbox"
                      checked={isVerified}
                      onChange={() => toggleVerified(c.id)}
                      className="mt-0.5 accent-hg-green flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wider font-sans text-hg-text-dim mb-0.5">
                        {cat}
                      </div>
                      <div className={`text-sm font-sans leading-snug ${isVerified ? 'text-hg-text-dim line-through' : 'text-hg-text'}`}>
                        "{c.text}"
                      </div>
                      {c.suggested_generic && !isVerified && (
                        <div className="text-[11px] text-hg-text-dim font-sans mt-1.5 italic">
                          Generic alternative: "{c.suggested_generic}"
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {!isVerified && (
                  <div className="flex items-center gap-2 mt-2 pl-6">
                    <button
                      data-testid={`${testid}-claim-${idx}-verify`}
                      onClick={() => toggleVerified(c.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-sans bg-hg-green/15 text-hg-green border border-hg-green/30 rounded-sm hover:bg-hg-green/25"
                    >
                      <CheckCircle2 size={10} strokeWidth={2} /> I confirm this is true
                    </button>
                    <button
                      data-testid={`${testid}-claim-${idx}-rewrite`}
                      onClick={() => handleRewrite(c)}
                      disabled={rewriting[c.id]}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green disabled:opacity-40"
                    >
                      {rewriting[c.id] ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} strokeWidth={1.5} />}
                      Rewrite generic
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="flex items-start gap-1.5 text-[11px] font-sans text-red-400">
          <AlertTriangle size={11} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X size={11} /></button>
        </div>
      )}
    </div>
  );
}
